import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ClientService } from '../service/client.service';
import { Client } from '../Module/Client.model';
import { StateService } from '../service/state.service';
import { map, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select-client-pop-up',
  templateUrl: './select-client-pop-up.component.html',
  styleUrls: ['./select-client-pop-up.component.css']
})
export class SelectClientPopUpComponent implements OnInit, OnDestroy {
  clients?: Client[] = [];
  clientsToDisplay?: Client[] = [];
  isLoading: boolean = false;
  searchQuery: string = '';
  
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private searchSubscription?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<SelectClientPopUpComponent>,
    private router: Router,
    private clientService: ClientService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.setupSearchObservable();
    this.loadClients();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  setupSearchObservable(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  loadClients(): void {
    this.isLoading = true;
    this.clientService.listClients().subscribe({
      next: (data: Client[]) => {
        this.clients = data;
        this.clientsToDisplay = this.clients.slice(0, 10);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading clients:', error);
        this.isLoading = false;
      }
    });
  }

  closeDialogAndGoToOrderPage(client: Client): void {
    this.stateService.setClient(client);
    const target = this.stateService.getTargetRoute();
    this.dialogRef.close();
    this.router.navigate([`/${target}`], { queryParams: { clientId: client.id } });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  goToAddClientForm(): void {
    this.dialogRef.close();
    this.router.navigate(['/addClient']);
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchQuery = inputElement.value;
    this.searchSubject.next(this.searchQuery);
  }

  performSearch(query: string): void {
    this.isLoading = true;
    
    setTimeout(() => {
      this.searchClients(query).subscribe({
        next: (clients) => {
          this.clientsToDisplay = clients;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error searching clients:', error);
          this.isLoading = false;
        }
      });
    }, 200);
  }

  searchClients(query: string): Observable<Client[]> {
    return of(this.clients ?? []).pipe(
      map(clients => {
        if (!query.trim()) {
          return clients.slice(0, 10); // Return first 10 clients if query is empty
        }
        
        const normalizedQuery = this.normalizeText(query);
        
        return clients.filter(client => {
          const normalizedFirstName = this.normalizeText(client.firstName || '');
          const normalizedLastName = this.normalizeText(client.lastName || '');
          const normalizedPhone = client.phoneNumber?.toString() || '';
          const normalizedEmail = this.normalizeText(client.email || '');
          
          return normalizedFirstName.includes(normalizedQuery) || 
                 normalizedLastName.includes(normalizedQuery) || 
                 normalizedPhone.includes(query) ||
                 normalizedEmail.includes(normalizedQuery);
        }).slice(0, 10);
      })
    );
  }
  
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
